import {faInfoCircle} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Table, TablePaginationConfig} from 'antd';
import {FilterValue} from 'antd/lib/table/interface';
import {useContext, useEffect, useState} from 'react';
import {ExtractRouteParams, useParams} from 'react-router';

import {appRoute} from '../../../../../../../app-route';
import {AlertFallback} from '../../../../../../../component/alert-fallback/alert-fallback';
import {PaginationControls} from '../../../../../../../layout/pagination-controls/pagination-controls';
import {Spinner} from '../../../../../../../layout/spinner/spinner';
import {useDomainConfig} from '../../../../../../../provider/domain-config/domain-config-hook';
import {Locale} from '../../../../../../../provider/locale/localization';
import {MainFilterContext} from '../../../../../../../provider/main-filter/main-filter';
import {MainFilterContextType} from '../../../../../../../provider/main-filter/main-filter-type';
import {useSourcesAccounts} from '../../../../../../../service/source-settings/accounts/accounts-hook';
import {SourcesAccountType} from '../../../../../../../service/source-settings/accounts/accounts-type';
import {CatalogByGroupType} from '../../../../../../../service/source-settings/catalogs/catalogs-type';
import {SourceLinkStatusEnum} from '../../../../../../../service/source-settings/source-links/source-links-type';
import {
    StoreAccountStatusEnum,
    useStoreAccountsList,
} from '../../../../../../../service/source-settings/store-accounts/store-accounts-hook';
import {useFormat} from '../../../../../../../util/format-hook/format-hook';
import {defaultPageSize} from '../../../../../../../util/pagination-hook/pagination-const';
import {EmptyTable} from '../empty-table/empty-table';

import {APP_STORE_CATALOG_ID, CATALOGS_WITHOUT_MANUAL_MANAGEMENT, statusFilterKey} from './accounts-table-const';
import {getAccountsTableColumns, getStoreAccountsTableColumns} from './accounts-table-helper';
import {SourceAccountsTableType, StoreAccountsTableType} from './accounts-table-type';
import {AddAccount} from './add-account/add-account';
import {AddAppstoreAccountModal} from './add-appstore-account/add-appstore-account-modal';
import {useAddAppstoreAccountForm} from './add-appstore-account/use-add-appstore-account-form';
import {useAccountsTableDataSource} from './use-accounts-table-data-source';
import * as styles from './accounts-table.scss';

type PropsType = {
    autoOpenAddAccount?: boolean;
    catalog: CatalogByGroupType;
    onAddAccountAutoOpen?: () => void;
};

type AccountsPayloadType = {
    count?: number | null;
    next?: string | null;
    previous?: string | null;
    results: Array<SourcesAccountType>;
};

// eslint-disable-next-line complexity, max-statements, sonarjs/cognitive-complexity
export function AccountsTable(props: PropsType): JSX.Element {
    const {autoOpenAddAccount = false, catalog, onAddAccountAutoOpen} = props;
    const {id, groupId} = useParams<
        ExtractRouteParams<typeof appRoute.settingsSourceSettingsAccount.path, string>
    >() || {
        id: '',
        groupId: 1,
    };
    const {getFormattedDateTime} = useFormat();
    const {companyName} = useDomainConfig();

    const {mainFilterKey} = useContext<MainFilterContextType>(MainFilterContext);
    const [cursor, setCursor] = useState<string | null>(null);
    const [pageSize, setPageSize] = useState<number>(defaultPageSize);
    const [statusFilter, setStatusFilter] = useState<Array<SourceLinkStatusEnum>>([]);
    const [storeAccountStatusFilter, setStoreAccountStatusFilter] = useState<Array<StoreAccountStatusEnum>>([]);

    const isAppStoreCatalog = catalog.id === APP_STORE_CATALOG_ID;

    const {
        result: sourceAccounts,
        isInProgress: isSourceAccountsInProgress,
        processError: sourceAccountsError,
        refresh: refreshSourceAccounts,
    } = useSourcesAccounts(
        {
            cursor,
            catalogId: Number(id),
            filterKey: mainFilterKey,
            pageSize,
            status: statusFilter,
        },
        {enabled: !isAppStoreCatalog}
    );
    const {
        data: storeAccountsData,
        isError: isStoreAccountsError,
        isFetching: isStoreAccountsFetching,
        isSuccess: isStoreAccountsSuccess,
        refetch: refetchStoreAccounts,
    } = useStoreAccountsList(
        {
            catalogId: catalog.id,
            cursor,
            pageSize,
            status: storeAccountStatusFilter,
        },
        {enabled: isAppStoreCatalog}
    );

    const accounts: AccountsPayloadType | null = isAppStoreCatalog ? null : sourceAccounts || null;
    const accountsCount = isAppStoreCatalog ? storeAccountsData?.count : accounts?.count;
    const {isInProgress, processError} = isAppStoreCatalog
        ? {isInProgress: isStoreAccountsFetching, processError: isStoreAccountsError}
        : {isInProgress: isSourceAccountsInProgress, processError: Boolean(sourceAccountsError)};
    const isStatusFilterEmpty = statusFilter.length === 0;
    const isStoreAccountStatusFilterEmpty = storeAccountStatusFilter.length === 0;
    const hasStoreAccounts = (storeAccountsData?.count ?? storeAccountsData?.results?.length ?? 0) > 0;
    const isAppStoreEmptyVisible =
        isAppStoreCatalog &&
        !isStoreAccountsFetching &&
        !isStoreAccountsError &&
        isStoreAccountsSuccess &&
        isStoreAccountStatusFilterEmpty &&
        !hasStoreAccounts;
    const isNonAppStoreEmptyVisible = !isAppStoreCatalog && accountsCount === 0 && isStatusFilterEmpty;
    const isAccountsToolbarHidden = isNonAppStoreEmptyVisible || isAppStoreEmptyVisible;
    const isAddButtonVisible = !CATALOGS_WITHOUT_MANUAL_MANAGEMENT.includes(catalog.id);

    useEffect(() => {
        setCursor(null);
        setStatusFilter([]);
        setStoreAccountStatusFilter([]);
    }, [mainFilterKey, pageSize]);

    function onTableChange(_pagination: TablePaginationConfig, filters: Record<string, FilterValue | null>): void {
        const selectedStatusFilter = filters[statusFilterKey] || [];
        const availableStatusFilter = Object.values(SourceLinkStatusEnum);
        const newStatusFilter = selectedStatusFilter.filter((value) =>
            availableStatusFilter.includes(value as SourceLinkStatusEnum)
        ) as Array<SourceLinkStatusEnum>;

        setCursor(null);
        setStatusFilter(newStatusFilter);
    }

    function onStoreTableChange(_pagination: TablePaginationConfig, filters: Record<string, FilterValue | null>): void {
        const selectedStatusFilter = filters[statusFilterKey] || [];
        const availableStatusFilter = Object.values(StoreAccountStatusEnum);
        const newStatusFilter = selectedStatusFilter.filter((value) =>
            availableStatusFilter.includes(value as StoreAccountStatusEnum)
        ) as Array<StoreAccountStatusEnum>;

        setCursor(null);
        setStoreAccountStatusFilter(newStatusFilter);
    }

    function refresh(): void {
        if (isAppStoreCatalog) {
            refetchStoreAccounts();

            return;
        }

        refreshSourceAccounts();
    }

    const appstoreAccountForm = useAddAppstoreAccountForm({
        autoOpen: autoOpenAddAccount,
        catalog,
        enabled: isAppStoreCatalog,
        onAutoOpen: onAddAccountAutoOpen,
        onSuccess: refresh,
    });

    const {sourceAccountsDataSource, storeAccountsDataSource} = useAccountsTableDataSource({
        accounts: accounts?.results ?? [],
        catalog,
        catalogGroupId: Number(groupId),
        onEditStoreAccount: appstoreAccountForm.handleOpenForEdit,
        onSuccess: refresh,
        storeAccounts: storeAccountsData?.results ?? [],
    });

    return (
        <>
            {isAppStoreCatalog && (
                <AddAppstoreAccountModal
                    catalog={catalog}
                    catalogGroupId={Number(groupId)}
                    formState={appstoreAccountForm}
                />
            )}

            {!isAccountsToolbarHidden && (
                <div className={styles.AccountsTable_controls}>
                    <span>
                        <Locale stringKey="TEXT__TOTAL" valueMap={{total: accountsCount || 0}} />
                    </span>

                    {isAddButtonVisible && (
                        <AddAccount
                            autoOpen={autoOpenAddAccount}
                            catalog={catalog}
                            catalogGroupId={Number(groupId)}
                            isPrimary
                            onAppstoreOpen={appstoreAccountForm.handleOpen}
                            onAutoOpen={onAddAccountAutoOpen}
                            onSuccess={refresh}
                        />
                    )}
                    {!isAddButtonVisible && (
                        <span className={styles.AccountsTable_disabledButtonLabel}>
                            <FontAwesomeIcon
                                className={styles.AccountsTable_disabledButtonLabelIcon}
                                icon={faInfoCircle}
                            />
                            <Locale
                                stringKey="SOURCE_SETTINGS__ACCOUNTS__DISABLED_MANUAL_MANAGEMENT"
                                valueMap={{companyName}}
                            />
                        </span>
                    )}
                </div>
            )}

            <div className={styles.AccountsTable_content}>
                <Spinner
                    isShow={
                        (isAppStoreCatalog &&
                            !isStoreAccountsSuccess &&
                            !isStoreAccountsError &&
                            isStoreAccountStatusFilterEmpty) ||
                        (!isAppStoreCatalog && isInProgress)
                    }
                    position="absolute"
                />

                {processError && <AlertFallback />}

                {isNonAppStoreEmptyVisible && (
                    <EmptyTable
                        subtitle={
                            <Locale
                                stringKey="SOURCE_SETTINGS__ACCOUNTS__DISABLED_MANUAL_MANAGEMENT"
                                valueMap={{companyName}}
                            />
                        }
                        title={<Locale stringKey="SOURCE_SETTINGS__ACCOUNTS__EMPTY__TITLE" />}
                    >
                        {isAddButtonVisible && (
                            <AddAccount
                                autoOpen={autoOpenAddAccount}
                                catalog={catalog}
                                catalogGroupId={Number(groupId)}
                                isPrimary
                                onAppstoreOpen={appstoreAccountForm.handleOpen}
                                onAutoOpen={onAddAccountAutoOpen}
                                onSuccess={refresh}
                            />
                        )}
                    </EmptyTable>
                )}

                {isAppStoreEmptyVisible && (
                    <EmptyTable
                        subtitle={<Locale stringKey="SOURCE_SETTINGS__ACCOUNTS__APPSTORE__EMPTY__SUBTITLE" />}
                        title={<Locale stringKey="SOURCE_SETTINGS__ACCOUNTS__EMPTY__TITLE" />}
                    >
                        {isAddButtonVisible && (
                            <AddAccount
                                autoOpen={autoOpenAddAccount}
                                catalog={catalog}
                                catalogGroupId={Number(groupId)}
                                isPrimary
                                onAppstoreOpen={appstoreAccountForm.handleOpen}
                                onAutoOpen={onAddAccountAutoOpen}
                                onSuccess={refresh}
                            />
                        )}
                    </EmptyTable>
                )}

                {isAppStoreCatalog &&
                    !processError &&
                    ((isStoreAccountsSuccess && hasStoreAccounts) || !isStoreAccountStatusFilterEmpty) && (
                        <>
                            <Table<StoreAccountsTableType>
                                columns={getStoreAccountsTableColumns()}
                                dataSource={storeAccountsDataSource}
                                loading={isStoreAccountsFetching}
                                onChange={onStoreTableChange}
                                pagination={false}
                                scroll={{x: true}}
                                size="middle"
                            />
                            {storeAccountsData && (
                                <PaginationControls
                                    onCursorChange={setCursor}
                                    onPageSizeChange={setPageSize}
                                    pageSize={pageSize}
                                    payload={storeAccountsData}
                                />
                            )}
                        </>
                    )}

                {accounts && !isNonAppStoreEmptyVisible && !processError && (
                    <>
                        <Table<SourceAccountsTableType>
                            columns={getAccountsTableColumns(getFormattedDateTime, Boolean(catalog.isKeyId))}
                            dataSource={sourceAccountsDataSource}
                            onChange={onTableChange}
                            pagination={false}
                            scroll={{x: true}}
                            size="middle"
                        />
                        <PaginationControls
                            onCursorChange={setCursor}
                            onPageSizeChange={setPageSize}
                            pageSize={pageSize}
                            payload={accounts}
                        />
                    </>
                )}
            </div>
        </>
    );
}
